import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Heart, ShoppingBag, Eye, X, Plus, Minus, Trash2, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProductsPage.css';

const PRICE_RANGES = [
  { label: 'Below ₹2,000', min: 0, max: 2000 },
  { label: '₹2,000 – ₹5,000', min: 2000, max: 5000 },
  { label: '₹5,000 – ₹10,000', min: 5000, max: 10000 },
  { label: '₹10,000 – ₹20,000', min: 10000, max: 20000 },
  { label: 'Over ₹20,000', min: 20000, max: Infinity }
];

const RING_SIZES = ['1', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30'];
const WEIGHT_RANGES = ['0-2g', '2-5g', '5-10g', '10-20g'];

const INLINE_VIDEOS = [
  { src: 'images/video b.mp4', titleKey: 'royalCraftsmanship' },
  { src: 'images/video j.mp4', titleKey: 'handcraftedHeritage' },
  { src: 'images/video p.mp4', titleKey: 'silverPureElegance' }
];

const resolveProductImg = (src) => {
  if (!src) return '/images/logo.png';
  if (typeof src !== 'string') return '/images/logo.png';
  const trimmed = src.trim();
  if (!trimmed) return '/images/logo.png';

  // 1. Data URLs (base64) & Blob URLs
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // 2. Absolute HTTP/HTTPS URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // 3. Already absolute path
  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  // 4. Starts with images/ or Images/
  if (trimmed.toLowerCase().startsWith('images/')) {
    return `/${trimmed}`;
  }

  // 5. Raw filename like "men chain11.jpg"
  return `/images/${trimmed}`;
};

const handleProductImgError = (e, product) => {
  const imgEl = e.currentTarget;
  const step = Number(imgEl.dataset.fallbackStep || '0');

  // Step 0: Try without spaces in /images/
  if (step === 0 && product?.image && typeof product.image === 'string') {
    imgEl.dataset.fallbackStep = '1';
    const cleanImg = product.image.replace(/^(\/?images\/)/i, '').replace(/\s+/g, '');
    imgEl.src = `/images/${cleanImg}`;
    return;
  }

  // Step 1: Try matched keywords from product name if applicable
  if (step <= 1 && product?.name) {
    imgEl.dataset.fallbackStep = '2';
    const lowerName = product.name.toLowerCase();
    const matchNumber = lowerName.match(/\d+/);
    const num = matchNumber ? matchNumber[0] : '';
    if (lowerName.includes('men') && lowerName.includes('chain')) {
      imgEl.src = `/images/men chain${num || '10'}.jpg`;
      return;
    }
    if (lowerName.includes('chain')) {
      imgEl.src = `/images/w chain${num || '1'}.jpg`;
      return;
    }
    if (lowerName.includes('ring')) {
      imgEl.src = lowerName.includes('men') ? `/images/men ring${num || '2'}.jpg` : `/images/w ring${num || '1'}.jpg`;
      return;
    }
    if (lowerName.includes('payal') || lowerName.includes('painjan')) {
      imgEl.src = `/images/painjan${num || '1'}.jpg`;
      return;
    }
    if (lowerName.includes('bracelet') || lowerName.includes('kada')) {
      imgEl.src = lowerName.includes('men') ? `/images/men br${num || '6'}.jpg` : `/images/w br${num || '1'}.jpg`;
      return;
    }
  }

  imgEl.src = '/images/logo.png';
};

const ProductCard = ({ p, onAddToCart, onToggleWishlist, onOpenDetail, isFav, t, cart, updateCartQuantity, lang }) => {
  const [hovered, setHovered] = useState(false);
  const isMr = lang === 'mr';
  const priceFont = isMr ? "'Poppins', sans-serif" : "'Playfair Display', serif";

  const getLocalizedContent = (product, field) => {
    if (lang === 'mr' && product[`${field}Mr`]) {
      return product[`${field}Mr`];
    }
    return product[field];
  };

  return (
    <motion.div
      className="pcard"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      layout
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
    >
      <div className="pcard-img-wrap" onClick={() => onOpenDetail(p, false)}>
        <img
          src={resolveProductImg(p.image)}
          alt={p.name}
          className="pcard-img"
          onError={(e) => handleProductImgError(e, p)}
        />

        <AnimatePresence>
          {hovered && (
            <motion.div
              className="pcard-hover-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <button className="pcard-quickview-btn" onClick={() => onOpenDetail(p, false)}>
                <Eye size={18} /> {t.quickView}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          className={`pcard-fav-btn ${isFav ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist(p.id);
          }}
          title={isFav ? 'Remove from Wishlist' : 'Add to Wishlist'}
        >
          <Heart size={16} fill={isFav ? '#e53a10' : 'none'} color={isFav ? '#e53a10' : '#ffffff'} />
        </button>

        <span className="pcard-material-badge">{p.material || p.subCategory}</span>
      </div>

      <div className="pcard-body">
        <span className="pcard-cat">{p.category}</span>
        <h3 className="pcard-name" onClick={() => onOpenDetail(p, false)}>
          {getLocalizedContent(p, 'name')}
        </h3>



        <div className="pcard-price-row">
          <span className="pcard-price" style={{ fontFamily: priceFont }}>
            <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
            <span className="pcard-amount" style={{ fontFamily: priceFont }}>{p.price.toLocaleString()}</span>
          </span>
        </div>

        <div className="pcard-actions">
          {(() => {
            const cartItem = (cart || []).find(item => item.product.id === p.id);
            if (cartItem) {
              return (
                <div className="amazon-qty-btn-group">
                  <button
                    className="amazon-qty-btn-minus"
                    onClick={() => updateCartQuantity(cartItem.itemKey || cartItem.product.id, cartItem.quantity - 1)}
                  >
                    {cartItem.quantity > 1 ? <Minus size={14} /> : <Trash2 size={14} />}
                  </button>
                  <span className="amazon-qty-value">{cartItem.quantity}</span>
                  <button
                    className="amazon-qty-btn-plus"
                    onClick={() => updateCartQuantity(cartItem.itemKey || cartItem.product.id, cartItem.quantity + 1)}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              );
            }
            return (
              <button className="pcard-btn-cart" onClick={() => onAddToCart(p, 1)}>
                <ShoppingBag size={15} /> {t.addToCart}
              </button>
            );
          })()}
          <button className="pcard-btn-buy" onClick={() => onOpenDetail(p, true)}>
            {t.buyNow}
          </button>
        </div>
      </div>

      <div className="pcard-shimmer"></div>
    </motion.div>
  );
};

const VideoCard = ({ videoSrc, titleKey, t, isMr }) => {
  return (
    <motion.div
      className="pcard-video-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <video src={videoSrc} autoPlay loop muted playsInline className="pcard-video" />
      <div className="pcard-video-overlay">
        <span className="pcard-video-badge">✦ GDR SHOWCASE ✦</span>
        <h4>{t[titleKey] || (isMr ? 'जीडीआर ज्वेलर्स' : titleKey)}</h4>
      </div>
    </motion.div>
  );
};

const ProductsPage = () => {
  const { products, categories, wishlist, addToCart, toggleWishlist, t, cart, updateCartQuantity, lang } = useContext(AppContext);
  const isMr = lang === 'mr';
  const priceFont = isMr ? "'Poppins', sans-serif" : "'Playfair Display', serif";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Build dynamic category hierarchy grouped by audience from Admin-created categories
  const dynamicFilterStructure = useMemo(() => {
    const structure = {};
    (categories || []).forEach(cat => {
      let aud = cat.audience || 'For Women';
      if (aud === 'Unisex / All' || aud === 'All' || aud === 'Unisex') {
        aud = isMr ? 'सर्व श्रेणी' : 'All Categories';
      }
      if (!structure[aud]) structure[aud] = [];
      // avoid duplicates by name in the same audience group
      if (!structure[aud].some(c => c.name.toLowerCase() === cat.name.toLowerCase())) {
        structure[aud].push(cat);
      }
    });
    return structure;
  }, [categories, isMr]);

  const getLocalizedContent = (product, field) => {
    if (lang === 'mr' && product[`${field}Mr`]) {
      return product[`${field}Mr`];
    }
    return product[field];
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedSubCats, setSelectedSubCats] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState('');
  const [selectedRingSizes, setSelectedRingSizes] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedWeightRanges, setSelectedWeightRanges] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [modalQty, setModalQty] = useState(1);
  const [isBuyNowFlow, setIsBuyNowFlow] = useState(false);

  useEffect(() => {
    document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const cat = searchParams.get('category');
    const subCat = searchParams.get('subcategory');
    const q = searchParams.get('search') || searchParams.get('q');

    if (cat) {
      const lowerCat = cat.toLowerCase();
      if (lowerCat.includes('women')) {
        setSelectedAudience('For Women');
      } else if (lowerCat.includes('men')) {
        setSelectedAudience('For Men');
      } else if (lowerCat.includes('baby') || lowerCat.includes('kid')) {
        setSelectedAudience('For Baby');
      } else {
        // e.g. cat = 'Chains' without audience
        setSelectedAudience('');
        setSelectedSubCats([cat]);
      }
    } else {
      setSelectedAudience('');
      setSelectedSubCats([]);
    }

    if (subCat) {
      setSelectedSubCats([subCat]);
    }

    if (q) {
      setSearchQuery(q);
    } else {
      setSearchQuery('');
    }
  }, [searchParams]);

  const togglePriceRange = (range) => {
    setSelectedPriceRanges(prev =>
      prev.some(r => r.label === range.label)
        ? prev.filter(r => r.label !== range.label)
        : [...prev, range]
    );
  };

  const handleCategoryClick = (audience, sc) => {
    const isGeneral = audience === 'All Categories' || audience === 'सर्व श्रेणी';
    if ((selectedAudience === audience || isGeneral) && selectedSubCats.length === 1 && selectedSubCats[0] === sc) {
      // Toggle off: clear subcategory selection
      setSelectedSubCats([]);
    } else {
      if (!isGeneral) {
        setSelectedAudience(audience);
      }
      setSelectedSubCats([sc]);
    }
  };

  const handleAudienceClick = (audience) => {
    if (selectedAudience === audience) {
      if (selectedSubCats.length > 0) {
        // Clear subcategories, show all for this audience
        setSelectedSubCats([]);
      } else {
        // Toggle off audience completely
        setSelectedAudience('');
      }
    } else {
      setSelectedAudience(audience);
      setSelectedSubCats([]);
    }
  };

  const isSubCatActive = (audience, sc) => {
    const isGeneral = audience === 'All Categories' || audience === 'सर्व श्रेणी';
    return (selectedAudience === audience || !selectedAudience || isGeneral) && selectedSubCats.includes(sc);
  };

  const toggleRingSize = (sz) => {
    setSelectedRingSizes(prev =>
      prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz]
    );
  };

  const toggleWeightRange = (w) => {
    setSelectedWeightRanges(prev =>
      prev.includes(w) ? prev.filter(s => s !== w) : [...prev, w]
    );
  };

  const clearAllFilters = () => {
    setSelectedPriceRanges([]);
    setSelectedSubCats([]);
    setSelectedAudience('');
    setSelectedRingSizes([]);
    setSelectedProductTypes([]);
    setSelectedWeightRanges([]);
    setSearchQuery('');
    navigate('/products', { replace: true });
  };

  const filteredProducts = products.filter(p => {
    // 1. Search Query Match
    const matchSearch = !searchQuery.trim() ||
      (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.nameMr && p.nameMr.includes(searchQuery)) ||
      (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.subCategory && p.subCategory.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.descriptionMr && p.descriptionMr.includes(searchQuery));

    // 2. Price Range Match
    const matchPrice = selectedPriceRanges.length === 0 || selectedPriceRanges.some(r => p.price >= r.min && p.price < r.max);

    // 3. Target Audience Group Match (e.g. For Women, For Men, For Baby)
    let matchAudience = true;
    if (selectedAudience) {
      const audLower = selectedAudience.toLowerCase();
      const pSub = (p.subCategory || '').toLowerCase();
      const pForWhom = (p.forWhom || '').toLowerCase();

      if (audLower.includes('women')) {
        matchAudience = pSub.includes('women') || pForWhom.includes('her') || pSub.includes('her');
      } else if (audLower.includes('men')) {
        matchAudience = (pSub.includes('men') && !pSub.includes('women')) || pForWhom.includes('him') || pSub.includes('him');
      } else if (audLower.includes('baby') || audLower.includes('kid')) {
        matchAudience = pSub.includes('baby') || pSub.includes('kid') || pForWhom.includes('baby');
      } else {
        matchAudience = pSub.includes(audLower) || audLower.includes(pSub);
      }
    }

    // 4. SubCategory / Category Match
    let matchSubCat = true;
    if (selectedSubCats.length > 0) {
      matchSubCat = selectedSubCats.some(sc => {
        if (!p.category) return false;
        const pCatLower = p.category.toLowerCase().trim();
        const scLower = sc.toLowerCase().trim();

        // Exact match
        if (pCatLower === scLower) {
          return true;
        }

        // Parentheses clean match: e.g. "Payal (Anklets)" matches "Payal" or "Painjan (Anklets)" matches "Painjan"
        const cleanSC = scLower.replace(/\(.*?\)/g, '').trim();
        const cleanPCat = pCatLower.replace(/\(.*?\)/g, '').trim();
        if (cleanSC && cleanPCat && (cleanSC === cleanPCat || pCatLower.includes(cleanSC) || scLower.includes(cleanPCat))) {
          return true;
        }

        // Substring / Normalized match
        const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (norm(p.category) === norm(sc)) {
          return true;
        }

        // Specific category mappings:
        // Chains: only matches chains
        if (scLower.includes('chain') && pCatLower.includes('chain')) return true;

        // Rings: only matches rings (excluding toe rings / jodvi)
        if (scLower === 'rings') {
          return pCatLower === 'rings' || (pCatLower.includes('ring') && !pCatLower.includes('toe') && !pCatLower.includes('jodvi'));
        }

        // Toe ring / Jodvi
        if (scLower.includes('jodvi') || scLower.includes('toe')) {
          return pCatLower.includes('jodvi') || pCatLower.includes('toe');
        }

        // Payal / Painjan / Anklets
        if (scLower.includes('payal') || scLower.includes('painjan') || scLower.includes('anklet')) {
          return pCatLower.includes('payal') || pCatLower.includes('painjan') || pCatLower.includes('anklet');
        }

        // Waist belt / Mekhala
        if (scLower.includes('mekhala') || scLower.includes('waist') || scLower.includes('kamar')) {
          return pCatLower.includes('mekhala') || pCatLower.includes('waist') || pCatLower.includes('kamar');
        }

        // Bracelets / Bangles
        if (scLower.includes('bracelet') || scLower.includes('bangle')) {
          return pCatLower.includes('bracelet') || pCatLower.includes('bangle');
        }

        // Kada
        if (scLower.includes('kada')) {
          return pCatLower.includes('kada');
        }

        // Kad-Dora
        if (scLower.includes('kad-dora') || scLower.includes('kaddora') || scLower.includes('dora')) {
          return pCatLower.includes('kad-dora') || pCatLower.includes('kaddora') || pCatLower.includes('dora');
        }

        // Necklace
        if (scLower.includes('necklace')) {
          return pCatLower.includes('necklace');
        }

        // Earrings
        if (scLower.includes('earring') || scLower.includes('jhumka')) {
          return pCatLower.includes('earring') || pCatLower.includes('jhumka');
        }

        return false;
      });
    }

    // 5. Additional Attributes Match
    const matchType = selectedProductTypes.length === 0 || selectedProductTypes.includes(p.category);
    const matchSize = selectedRingSizes.length === 0 || (p.ringSize && selectedRingSizes.includes(p.ringSize.toString()));
    const matchWeight = selectedWeightRanges.length === 0 || (p.weight && selectedWeightRanges.includes(p.weight));

    return matchSearch && matchPrice && matchAudience && matchSubCat && matchType && matchSize && matchWeight;
  });

  const getRelated = (product) =>
    products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const openModal = (product, buyNow = false) => {
    setSelectedProduct(product);
    setModalQty(1);
    setSelectedSize(product.ringSize || 'Standard');
    setIsBuyNowFlow(buyNow);
  };

  const gridElements = [];
  filteredProducts.forEach((p, idx) => {
    gridElements.push(
      <ProductCard
        key={p.id}
        p={p}
        isFav={wishlist.includes(p.id)}
        onAddToCart={addToCart}
        onToggleWishlist={toggleWishlist}
        onOpenDetail={openModal}
        t={t}
        cart={cart}
        updateCartQuantity={updateCartQuantity}
        lang={lang}
      />
    );
    if ((idx + 1) % 6 === 0) {
      const vIndex = Math.floor((idx + 1) / 6) - 1;
      const video = INLINE_VIDEOS[vIndex % INLINE_VIDEOS.length];
      gridElements.push(<VideoCard key={`vid-${idx}`} videoSrc={video.src} titleKey={video.titleKey} t={t} isMr={isMr} />);
    }
  });

  return (
    <div className="products-page-v2">
      <div className="pp-layout">
        <aside className="pp-sidebar-expanded">
          <div className="pp-sidebar-header">
            <h3>{t.filters}</h3>
            {(selectedPriceRanges.length > 0 || selectedSubCats.length > 0 || selectedAudience || selectedRingSizes.length > 0 || selectedProductTypes.length > 0 || selectedWeightRanges.length > 0) && (
              <button className="pp-clear-all" onClick={clearAllFilters}>{t.clearAll}</button>
            )}
          </div>

          <div className="filter-card-block">
            <h4>{isMr ? 'सर्व श्रेणी' : 'All Categories'}</h4>
            {Object.keys(dynamicFilterStructure).length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#888', fontStyle: 'italic', padding: '8px 0' }}>
                {isMr ? 'कोणतीही श्रेणी उपलब्ध नाही' : 'No categories available'}
              </p>
            ) : (
              Object.entries(dynamicFilterStructure).map(([audience, catList]) => (
                <div key={audience} className="pp-audience-group">
                  <button
                    className={`pp-audience-label ${selectedAudience === audience ? 'active' : ''}`}
                    onClick={() => handleAudienceClick(audience)}
                  >
                    {audience.toUpperCase()}
                  </button>
                  <div className="pp-subcat-list">
                    {catList.map(cat => (
                      <button
                        key={cat.id || cat.name}
                        className={`pp-subcat-item ${isSubCatActive(audience, cat.name) ? 'active' : ''}`}
                        onClick={() => handleCategoryClick(audience, cat.name)}
                      >
                        {isMr ? (cat.nameMr || cat.name) : cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="filter-card-block">
            <h4>{t.ringSize}</h4>
            <div className="ring-sizes-grid">
              {RING_SIZES.map(sz => (
                <button
                  key={sz}
                  className={`ring-size-btn ${selectedRingSizes.includes(sz) ? 'active' : ''}`}
                  onClick={() => toggleRingSize(sz)}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-card-block">
            <h4>{t.weightRange}</h4>
            <div className="filter-chips-grid">
              {WEIGHT_RANGES.map(w => (
                <button
                  key={w}
                  className={`filter-chip ${selectedWeightRanges.includes(w) ? 'active' : ''}`}
                  onClick={() => toggleWeightRange(w)}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-card-block">
            <h4>{t.priceRange}</h4>
            {PRICE_RANGES.map(range => (
              <label key={range.label} className="pp-checkbox-row">
                <input
                  type="checkbox"
                  checked={selectedPriceRanges.some(r => r.label === range.label)}
                  onChange={() => togglePriceRange(range)}
                />
                <span className="pp-checkbox-custom"></span>
                <span className="pp-checkbox-label">{range.label}</span>
              </label>
            ))}
          </div>
        </aside>

        <main className="pp-main">
          <section className="pp-concept-banner">
            <div className="pp-concept-bg">
              <img src="images/p banner.png" alt="Products Banner" />
              <div className="pp-concept-gradient"></div>
            </div>
            <div className="pp-concept-content">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {t.exquisiteCollection}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.4 }}
              >
                {t.bannerDesc}
              </motion.p>
            </div>
          </section>

          <div className="pp-listing-header">
            <div className="pp-listing-header-top">
              <span className="pp-count-badge">
                <strong>{filteredProducts.length}</strong> {isMr ? 'दागिने सापडले' : `piece${filteredProducts.length !== 1 ? 's' : ''} found`}
              </span>

              <div className="pp-search-box">
                <Search size={18} className="pp-search-icon" />
                <input
                  type="text"
                  placeholder={isMr ? 'दागिने शोधा (उदा. रिंग, पैंजण, चेन)...' : 'Search jewellery by name, category...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="pp-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {(selectedSubCats.length > 0 || selectedAudience || searchQuery || selectedRingSizes.length > 0 || selectedProductTypes.length > 0 || selectedWeightRanges.length > 0 || selectedPriceRanges.length > 0) && (
              <div className="pp-active-filters">
                <span className="pp-active-label">{isMr ? 'सक्रिय फिल्टर:' : 'Active Filters:'}</span>
                {searchQuery && (
                  <span className="pp-filter-tag search-tag">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery('')}>×</button>
                  </span>
                )}
                {selectedAudience && (
                  <span className="pp-filter-tag">
                    {selectedAudience}
                    <button onClick={() => { setSelectedAudience(''); setSelectedSubCats([]); }}>×</button>
                  </span>
                )}
                {selectedSubCats.map(sc => (
                  <span key={sc} className="pp-filter-tag">
                    {sc}
                    <button onClick={() => setSelectedSubCats(prev => prev.filter(s => s !== sc))}>×</button>
                  </span>
                ))}
                {selectedPriceRanges.map(pr => (
                  <span key={pr.label} className="pp-filter-tag">
                    {pr.label}
                    <button onClick={() => togglePriceRange(pr)}>×</button>
                  </span>
                ))}
                {selectedRingSizes.map(sz => (
                  <span key={sz} className="pp-filter-tag">
                    Size: {sz}
                    <button onClick={() => toggleRingSize(sz)}>×</button>
                  </span>
                ))}
                {selectedWeightRanges.map(w => (
                  <span key={w} className="pp-filter-tag">
                    {w}
                    <button onClick={() => toggleWeightRange(w)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="pp-empty-state">
              <p>{isMr ? 'निवडलेल्या फिल्टरशी जुळणारे कोणतेही दागिने नाहीत.' : 'No products match the selected filters.'}</p>
              <button onClick={clearAllFilters}>{t.clearAll}</button>
            </div>
          ) : (
            <motion.div className="pp-grid" layout>
              <AnimatePresence>
                {gridElements}
              </AnimatePresence>
            </motion.div>
          )}
        </main>
      </div>

      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            className="pp-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setSelectedProduct(null)}
          >
            <motion.div
              className="pp-modal pp-modal-noscroll"
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 30 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
              <button className="pp-modal-close" onClick={() => setSelectedProduct(null)}>
                <X size={22} />
              </button>

              <div className="pp-modal-main">
                <div className="pp-modal-img-col">
                  <img
                    src={resolveProductImg(selectedProduct.image)}
                    alt={selectedProduct.name}
                    onError={(e) => handleProductImgError(e, selectedProduct)}
                  />
                </div>

                <div className="pp-modal-info-col">
                  <span className="pp-modal-cat">{selectedProduct.category}</span>
                  <h2>
                    {getLocalizedContent(selectedProduct, 'name')}
                  </h2>

                  <div className="pp-modal-price" style={{ fontFamily: priceFont }}>
                    <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                    <span className="pcard-amount" style={{ fontFamily: priceFont }}>{selectedProduct.price.toLocaleString()}</span>
                  </div>

                  {(selectedProduct.material || (selectedProduct.weight && selectedProduct.weight !== '0' && selectedProduct.weight !== 0) || selectedProduct.subCategory) && (
                    <div className="pp-modal-details">
                      {selectedProduct.material ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'धातू' : 'Material'}:</span>
                          <span className="detail-value">{selectedProduct.material}</span>
                        </div>
                      ) : null}
                      {selectedProduct.weight && selectedProduct.weight !== '0' && selectedProduct.weight !== 0 ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'वजन' : 'Weight'}:</span>
                          <span className="detail-value">{typeof selectedProduct.weight === 'number' || !String(selectedProduct.weight).toLowerCase().includes('g') ? `${selectedProduct.weight} g` : selectedProduct.weight}</span>
                        </div>
                      ) : null}
                      {selectedProduct.subCategory ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'श्रेणी' : 'Category'}:</span>
                          <span className="detail-value">{selectedProduct.subCategory}</span>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <p className="pp-modal-desc">
                    {getLocalizedContent(selectedProduct, 'description')}
                  </p>

                  <div className="pp-sizes-group">
                    <span className="sizes-label">{t.availableSizes}</span>
                    <div className="sizes-chips">
                      {(selectedProduct.category?.toLowerCase().includes('ring')
                        ? ['6', '7', '8', '9', '10', '12', '14', '16', '18', '20']
                        : selectedProduct.category?.toLowerCase().includes('chain')
                          ? ['16"', '18"', '20"', '22"']
                          : ['S', 'M', 'L', 'Free Size']
                      ).map(sz => (
                        <button
                          key={sz}
                          className={`size-chip ${selectedSize === sz ? 'active' : ''}`}
                          onClick={() => setSelectedSize(sz)}
                        >
                          {sz}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pp-modal-qty-row">
                    <span>{t.quantity}</span>
                    <div className="pp-qty-control">
                      <button onClick={() => setModalQty(q => Math.max(1, q - 1))}><Minus size={14} /></button>
                      <span>{modalQty}</span>
                      <button onClick={() => setModalQty(q => q + 1)}><Plus size={14} /></button>
                    </div>
                    <span className="pp-modal-subtotal" style={{ fontFamily: priceFont }}>
                      <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                      <span className="pcard-amount" style={{ fontFamily: priceFont }}>{(selectedProduct.price * modalQty).toLocaleString()}</span>
                    </span>
                  </div>

                  <div className="pp-modal-btns">
                    <button
                      className="btn-pp-add-cart"
                      onClick={() => {
                        addToCart(selectedProduct, modalQty, selectedSize);
                        if (isBuyNowFlow) {
                          setSelectedProduct(null);
                          navigate(`/contact?subject=Buy Now - ${encodeURIComponent(selectedProduct.name)} (Size: ${selectedSize || 'Standard'})`);
                        } else {
                          setSelectedProduct(null);
                        }
                      }}
                    >
                      <ShoppingBag size={16} /> {isBuyNowFlow ? t.buyNow : `${t.addToCart} (${modalQty})`}
                    </button>
                    {!isBuyNowFlow && (
                      <button
                        className="btn-pp-buy-now"
                        onClick={() => {
                          addToCart(selectedProduct, modalQty, selectedSize);
                          setSelectedProduct(null);
                          navigate(`/contact?subject=Buy Now - ${encodeURIComponent(selectedProduct.name)} (Size: ${selectedSize || 'Standard'})`);
                        }}
                      >
                        {t.buyNow}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {getRelated(selectedProduct).length > 0 && (
                <div className="pp-modal-related">
                  <h3>{t.samePattern}</h3>
                  <div className="pp-related-grid">
                    {getRelated(selectedProduct).map(rp => (
                      <motion.div
                        key={rp.id}
                        className="pp-related-card"
                        whileHover={{ scale: 1.03 }}
                        onClick={() => openModal(rp, false)}
                      >
                        <img src={rp.image} alt={rp.name} />
                        <h5>{getLocalizedContent(rp, 'name')}</h5>
                        <p className="pp-related-price" style={{ fontFamily: priceFont }}>
                          <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                          <span className="pcard-amount" style={{ fontFamily: priceFont }}>{rp.price.toLocaleString()}</span>
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsPage;
